import React, { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Plus, Loader2, UserCog } from "lucide-react";
import { toast } from "sonner";

const ROLES = [["admin", "Administrator"], ["pm", "Project Manager"], ["labor_manager", "Labor Manager"]];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const empty = { email: "", name: "", role: "pm", password: "", active: true };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, name: u.name, role: u.role, password: "", active: u.active !== false }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.patch(`/users/${editing.id}`, form);
      else await api.post("/users", form);
      toast.success(editing ? "User updated" : "User created");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-bold text-slate-900">Users & Permissions</h1>
        <Button onClick={openNew} className="bg-[#E8791A] hover:bg-[#D66A0F] text-white" data-testid="new-user-button"><Plus className="w-4 h-4 mr-1" />Add User</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
        {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0B192C]" /></div> : (
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr>
              <th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">Role</th><th className="px-3 py-2 text-left">Status</th><th></th>
            </tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                  <td className="px-3 py-2 font-medium">{u.name}</td>
                  <td className="px-3 py-2">{u.email}</td>
                  <td className="px-3 py-2"><span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{u.role_label}</span></td>
                  <td className="px-3 py-2">{u.active === false ? <span className="text-red-600">Disabled</span> : <span className="text-emerald-700">Active</span>}</td>
                  <td className="px-3 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}><UserCog className="w-4 h-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">{editing ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" className="mt-1" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" className="mt-1" /></div>
            <div><Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">{editing ? "New Password (leave blank to keep)" : "Password"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" className="mt-1" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-active-switch" /><Label>Active</Label></div>
          </div>
          <DialogFooter className="mt-2"><Button onClick={save} disabled={saving || !form.email || !form.name} className="bg-[#0B192C]" data-testid="user-save-button">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
